/* People who need what you offer.
 *
 * A SCREEN, not a dialog. As a modal this had to be a DIGEST — five rows and
 * then "more people need this, go and look on the Prayer page", because a
 * dialog cannot hold a list that grows. That is a strange thing to tell
 * somebody who has just said they can help: the whole point is to show them who
 * to help.
 *
 * With a page it scrolls, so there is no cap and no apology. The two groups
 * stay, because they are genuinely different things.
 */
import { ArrowLeft, HandHeart, Loader2 } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import { useHelpMatches, useHelpOptions } from '@/api/queries'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { MatchRow } from './HelpDialog'

export function HelpMatchesPage() {
  const [params] = useSearchParams()
  /* The kinds ride in the URL so the page can be refreshed, shared, or reached
     from a notification — a dialog kept them in component state, where they
     died on reload. */
  const kinds = (params.get('kinds') ?? '').split(',').filter(Boolean)

  const options = useHelpOptions()
  const matches = useHelpMatches(kinds)
  const sentinel = useInfiniteScroll(() => matches.fetchNextPage(), {
    enabled: matches.hasNextPage && !matches.isFetchingNextPage,
  })

  const all = matches.data?.pages.flatMap((p) => p.items) ?? []

  /* TWO GROUPS, because they are not the same thing.
   *
   * A request the author tagged "a job referral" is a real match for somebody
   * offering to hire. An untagged one is a maybe — included because a need
   * nobody labelled is still a need, but presenting the two together made the
   * feature look broken: ticking "I can hire" showed prayers about exams with
   * nothing to say why they were there. */
  const tagged = all.filter((r) => r.help_kind)
  const untagged = all.filter((r) => !r.help_kind)

  const labels = kinds
    .map((k) => options.data?.find((o) => o.kind === k)?.label)
    .filter(Boolean)

  return (
    <div>
      <div className="mb-s2 flex items-center gap-s3">
        <Link
          to="/prayer"
          aria-label="Back to Prayer Requests"
          className="grid h-9 w-9 place-items-center rounded-pill text-ink-2 transition-colors hover:bg-hover"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden />
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">People who need this</h1>
      </div>

      {labels.length ? (
        <p className="mb-s4 pl-[52px] text-base text-ink-2">
          You offered: {labels.join(', ')}
        </p>
      ) : null}

      {matches.isLoading ? (
        <div className="flex justify-center py-s8">
          <Loader2
            className="h-5 w-5 animate-spin text-ink-2"
            aria-label="Loading"
          />
        </div>
      ) : null}

      {!matches.isLoading && !all.length ? (
        <div className="rounded-md bg-surface px-s5 py-s8 text-center shadow-1">
          <HandHeart className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
          <p className="mt-s3 text-md font-bold">Nobody needs this right now</p>
          <p className="mt-1 text-base text-ink-2">
            Nobody in your church has asked for this yet. You will hear when
            somebody does.
          </p>
        </div>
      ) : null}

      {all.length ? (
        <div className="overflow-hidden rounded-md bg-surface shadow-1">
          {tagged.length ? (
            <ul className="divide-y divide-line-2">
              {tagged.map((request) => (
                <MatchRow key={request.id} request={request} />
              ))}
            </ul>
          ) : null}

          {/* Said plainly rather than mixed in: these are not matches, they are
              requests whose author did not say what would help. */}
          {untagged.length ? (
            <>
              <p className="border-t border-line-2 bg-surface-2 px-s4 py-s2 text-sm font-semibold text-ink-2">
                {tagged.length
                  ? 'Others who may need help'
                  : 'Nobody asked for this directly, but these may need help'}
              </p>
              <ul className="divide-y divide-line-2">
                {untagged.map((request) => (
                  <MatchRow key={request.id} request={request} />
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}

      <div ref={sentinel} className="h-px" aria-hidden />

      {matches.isFetchingNextPage ? (
        <div className="flex justify-center py-s4">
          <Loader2
            className="h-5 w-5 animate-spin text-ink-2"
            aria-label="Loading more"
          />
        </div>
      ) : null}
    </div>
  )
}
